import { createContext, useContext, useEffect, useMemo, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

type AppRole = 'admin' | 'user' | 'producer';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  roles: AppRole[];
  isLoading: boolean;
  rolesLoading: boolean;
  isAdmin: boolean;
  isProducer: boolean;
  signOut: () => Promise<void>;
  becomeProducer: () => Promise<boolean>;
  refreshRoles: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);
  const claimedRef = useRef(false); // Track if we've already tried to claim
  // Incident guardrail: prevent auth bootstrap fanout for the same user session.
  const bootstrappedAuthKeyRef = useRef<string | null>(null);
  const bootstrappingAuthKeyRef = useRef<string | null>(null);
  const roleBootstrapPromiseRef = useRef<Promise<void> | null>(null);

  // Claim guest bookings/orders after login – best-effort, never blocks UI
  const claimGuestData = async () => {
    if (claimedRef.current) return;
    claimedRef.current = true;

    try {
      // Ensure we have a fresh (non-expired) session before invoking.
      // onAuthStateChange can fire SIGNED_IN with a stale token before
      // auto-refresh completes, which causes a 401 from the edge function.
      const { data: { session: freshSession } } = await supabase.auth.getSession();
      if (!freshSession) {
        claimedRef.current = false; // Allow retry on next real sign-in
        return;
      }

      const { data, error } = await supabase.functions.invoke('claim-guest-data');
      
      if (error) {
        // Non-critical: don't block page rendering, just warn
        console.warn('[claim-guest-data] Skipped – edge function error:', error.message ?? error);
        return;
      }

      const claimedBookings = data?.claimed_bookings ?? 0;
      const claimedOrders = data?.claimed_orders ?? 0;

      if (claimedBookings > 0 || claimedOrders > 0) {
        const parts: string[] = [];
        if (claimedBookings > 0) {
          parts.push(`${claimedBookings} ${claimedBookings === 1 ? 'bokning' : 'bokningar'}`);
        }
        if (claimedOrders > 0) {
          parts.push(`${claimedOrders} ${claimedOrders === 1 ? 'order' : 'ordrar'}`);
        }
        
        toast({
          title: 'Välkommen tillbaka!',
          description: `Vi kopplade ${parts.join(' och ')} till ditt konto.`,
        });
      }
    } catch (err) {
      // Best-effort – never let claim failures propagate
      console.warn('[claim-guest-data] Skipped – exception:', err);
    }
  };

  const fetchRoles = async (userOverride?: User | null) => {
    const effectiveUser = userOverride ?? user;

    if (!effectiveUser) {
      setRoles([]);
      setRolesLoading(false);
      return;
    }

    setRolesLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_user_roles');
      if (error) {
        console.error('Error fetching roles:', error);
        setRoles([]);
      } else {
        setRoles((data as AppRole[]) || []);
      }
    } catch (err) {
      console.error('Exception fetching roles:', err);
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  };

  const ensureUserRole = async (hasUser: boolean) => {
    if (!hasUser) return;
    
    try {
      await supabase.rpc('ensure_user_role');
    } catch (err) {
      console.error('Exception ensuring user role:', err);
    }
  };

  const refreshRoles = async () => {
    await fetchRoles();
  };

  const getAuthBootstrapKey = (targetUser: User, sessionOverride?: Session | null) => {
    const sourceSession = sessionOverride ?? session;
    const sessionToken = sourceSession?.refresh_token ?? sourceSession?.access_token ?? 'no-session-token';
    return `${targetUser.id}:${sessionToken}`;
  };

  const bootstrapRolesForUser = async (targetUser: User, sessionOverride?: Session | null) => {
    const authKey = getAuthBootstrapKey(targetUser, sessionOverride);

    // Strict once-per-session-per-user guard.
    if (bootstrappedAuthKeyRef.current === authKey) {
      return;
    }

    if (bootstrappingAuthKeyRef.current === authKey && roleBootstrapPromiseRef.current) {
      await roleBootstrapPromiseRef.current;
      return;
    }

    bootstrappingAuthKeyRef.current = authKey;

    const bootstrapPromise = (async () => {
      await ensureUserRole(true);
      await fetchRoles(targetUser);
    })();

    roleBootstrapPromiseRef.current = bootstrapPromise;

    try {
      await bootstrapPromise;
      bootstrappedAuthKeyRef.current = authKey;
    } finally {
      if (roleBootstrapPromiseRef.current === bootstrapPromise) {
        roleBootstrapPromiseRef.current = null;
      }
      if (bootstrappingAuthKeyRef.current === authKey) {
        bootstrappingAuthKeyRef.current = null;
      }
    }
  };

  const becomeProducer = async (): Promise<boolean> => {
    try {
      const { error } = await supabase.rpc('become_producer');
      if (error) {
        console.error('Error becoming producer:', error);
        return false;
      }
      await fetchRoles();
      return true;
    } catch (err) {
      console.error('Exception becoming producer:', err);
      return false;
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRoles([]);
    setRolesLoading(false);
    bootstrappedAuthKeyRef.current = null;
    bootstrappingAuthKeyRef.current = null;
    roleBootstrapPromiseRef.current = null;
    claimedRef.current = false;
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);

        // Compute auth key for this session to detect no-op auth events
        const newAuthKey = newSession?.user ? getAuthBootstrapKey(newSession.user, newSession) : null;

        // If we've already bootstrapped for this exact session token, avoid toggling
        // `rolesLoading` or re-running the bootstrap flow. This prevents transient
        // full-page loaders when the auth library emits visibility/focus-driven
        // state events without a real session change (common on mobile Safari).
        if (newAuthKey && bootstrappedAuthKeyRef.current === newAuthKey) {
          // No-op: keep existing roles/rolesLoading state and don't trigger bootstrap
          setRolesLoading(false);
          setIsLoading(false);
          return;
        }

        // Ensure we don't accidentally mark roles as "loaded" before the new user is available.
        setRolesLoading(!!newSession?.user);

        // Defer role fetching to avoid deadlock
        if (newSession?.user) {
          setTimeout(() => {
            bootstrapRolesForUser(newSession.user, newSession).finally(() => setIsLoading(false));
            // Sync user into user_directory (idempotent, best-effort)
            supabase.rpc('ensure_user_in_directory').then(({ error }) => {
              if (error) console.warn('[ensure_user_in_directory]', error.message);
            });

            // Claim guest data on SIGNED_IN event (login or signup)
            if (event === 'SIGNED_IN') {
              claimGuestData();
            }
          }, 0);
        } else {
          setRoles([]);
          setRolesLoading(false);
          setIsLoading(false);
          bootstrappedAuthKeyRef.current = null;
          bootstrappingAuthKeyRef.current = null;
          roleBootstrapPromiseRef.current = null;
          // Reset claimed flag on sign out so next login will try to claim
          claimedRef.current = false;
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      
      if (existingSession?.user) {
        bootstrapRolesForUser(existingSession.user, existingSession)
          .finally(() => setIsLoading(false));
      } else {
        setIsLoading(false);
        setRolesLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Derive role flags ONLY after roles have finished loading.
  // This prevents premature redirects based on an empty roles array.
  const isAdmin = useMemo(() => {
    if (rolesLoading) return false;
    return roles.includes('admin');
  }, [roles, rolesLoading]);

  const isProducer = useMemo(() => {
    if (rolesLoading) return false;
    return roles.includes('producer');
  }, [roles, rolesLoading]);

  const value: AuthContextValue = {
    user,
    session,
    roles,
    isLoading,
    rolesLoading,
    isAdmin,
    isProducer,
    signOut,
    becomeProducer,
    refreshRoles,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
